import React from 'react';
import { useImmer } from 'use-immer';
import { AppStateContext, defaultAppState } from '@/shared/contexts';
import {
  localStorageLabels,
  syncStateIntoStorageService,
  syncStorageIntoStateService,
} from '@/utils';

export const AppStateContextProvider = ({ children }) => {
  const { appState, setAppState } = useAppStateContextProvider();

  return (
    <AppStateContext.Provider value={{ appState, setAppState }}>
      {children}
    </AppStateContext.Provider>
  );
};

// Custom hook to manage and sync app state with localStorage
const useAppStateContextProvider = () => {
  // Use Immer for immutable state updates
  const [appState, setAppState] = useImmer(defaultAppState);

  // Helper to validate TID (must be a non-empty string)
  const validateTid = (tid) =>
    typeof tid === 'string' && tid?.trim().length > 0;

  // Sync TID between state and localStorage
  React.useMemo(() => {
    const tid = appState?.tid;
    if (validateTid(tid))
      // If TID is valid, save to localStorage
      syncStateIntoStorageService({ value: tid, key: localStorageLabels.TID });
    else {
      // Otherwise, try to load from localStorage into state
      const tid = syncStorageIntoStateService(localStorageLabels.TID);
      if (validateTid(tid))
        setAppState((draft) => {
          draft.tid = tid;
        });
    }
  }, [appState?.tid]);

  // Sync localData between state and localStorage
  React.useMemo(() => {
    const localData = appState?.localData;

    if (localData?.config?.id) {
      // If localData has a config id, save to localStorage
      syncStateIntoStorageService({
        key: localStorageLabels.LOCAL_DATA,
        value: localData,
      });
    } else {
      // Otherwise, try to load from localStorage into state
      const storedLocalData = syncStorageIntoStateService(
        localStorageLabels.LOCAL_DATA,
      );

      if (storedLocalData)
        setAppState((draft) => {
          draft.localData = storedLocalData;
        });
    }
  }, [appState?.localData]);

  // Sync deviceInfo between state and localStorage
  React.useMemo(() => {
    const deviceInfo = appState?.deviceInfo;

    if (deviceInfo?.deviceId) {
      // If deviceInfo has a deviceId, save to localStorage
      syncStateIntoStorageService({
        key: localStorageLabels.DEVICE_INFO,
        value: deviceInfo,
      });
    } else {
      // Otherwise, try to load from localStorage into state
      const storedDeviceInfo = syncStorageIntoStateService(
        localStorageLabels.DEVICE_INFO,
      );

      if (storedDeviceInfo)
        setAppState((draft) => {
          draft.deviceInfo = storedDeviceInfo;
        });
    }
  }, [appState?.deviceInfo]);

  // Sync proofs between state and localStorage
  React.useMemo(() => {
    const proofs = appState?.proofs;

    if (Array.isArray(proofs?.data) && proofs?.data?.length > 0) {
      // If proofs has data, save to localStorage
      syncStateIntoStorageService({
        key: localStorageLabels.PROOF,
        value: proofs,
      });
    } else {
      // Otherwise, try to load from localStorage into state
      const storedProofs = syncStorageIntoStateService(
        localStorageLabels.PROOF,
      );

      if (storedProofs)
        setAppState((draft) => {
          draft.proofs = storedProofs;
        });
    }
  }, [appState?.proofs?.data]);

  // Sync deviceMeta between state and localStorage
  React.useMemo(() => {
    const deviceMeta = appState?.deviceMeta;

    if (deviceMeta) {
      syncStateIntoStorageService({
        key: localStorageLabels.DEVICE_META,
        value: deviceMeta,
      });
    } else {
      const storedDeviceMeta = syncStorageIntoStateService(
        localStorageLabels.DEVICE_META,
      );

      if (storedDeviceMeta)
        setAppState((draft) => {
          draft.deviceMeta = storedDeviceMeta;
        });
    }
  }, [appState?.deviceMeta]);

  // Return state and updater for context provider
  return { appState, setAppState };
};
